import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'

export type AppToastTone = 'success' | 'error' | 'info'

type AppToastPayload = {
  tone?: AppToastTone
  title: string
  message?: string
  durationMs?: number
}

type AppToast = Required<Pick<AppToastPayload, 'tone' | 'title'>> & {
  id: string
  message: string
  durationMs: number
}

const APP_TOAST_EVENT = 'dihaw:toast'
const DEFAULT_TOAST_DURATION_MS = 4200

export function showAppToast(payload: AppToastPayload) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(APP_TOAST_EVENT, { detail: payload }))
}

export function AppToastViewport() {
  const [toasts, setToasts] = useState<AppToast[]>([])
  const timersRef = useRef<Record<string, number>>({})

  useEffect(() => {
    const dismissToast = (id: string) => {
      window.clearTimeout(timersRef.current[id])
      delete timersRef.current[id]
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }

    const handleToast = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail as AppToastPayload : null
      if (!detail?.title) return
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
      const toast: AppToast = {
        id,
        tone: detail.tone || 'info',
        title: detail.title,
        message: detail.message || '',
        durationMs: detail.durationMs || DEFAULT_TOAST_DURATION_MS,
      }
      setToasts((current) => [toast, ...current].slice(0, 4))
      timersRef.current[id] = window.setTimeout(() => dismissToast(id), toast.durationMs)
    }

    window.addEventListener(APP_TOAST_EVENT, handleToast)
    return () => {
      window.removeEventListener(APP_TOAST_EVENT, handleToast)
      Object.values(timersRef.current).forEach((timerId) => window.clearTimeout(timerId))
      timersRef.current = {}
    }
  }, [])

  if (toasts.length === 0) return null

  return (
    <section className="app-toast-viewport" aria-live="polite" aria-label="Notifikasi">
      {toasts.map((toast) => {
        const Icon = toast.tone === 'success' ? CheckCircle2 : toast.tone === 'error' ? AlertTriangle : Info
        return (
          <article key={toast.id} className={`app-toast is-${toast.tone}`}>
            <span className="app-toast-icon">
              <Icon size={20} />
            </span>
            <div>
              <strong>{toast.title}</strong>
              {toast.message ? <p>{toast.message}</p> : null}
            </div>
            <button
              type="button"
              onClick={() => setToasts((current) => current.filter((item) => item.id !== toast.id))}
              aria-label="Tutup notifikasi"
            >
              <X size={17} />
            </button>
          </article>
        )
      })}
    </section>
  )
}
